import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});
  const load = () => api.get("/admin/inventory").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (slug, key, val) => setEdits((e) => ({ ...e, [slug]: { ...e[slug], [key]: val } }));
  const save = async (slug) => {
    const payload = edits[slug]; if (!payload) return;
    const clean = {};
    for (const k of Object.keys(payload)) {
      if (k === "stock" || k === "reserved" || k === "incoming") clean[k] = Number(payload[k]);
      else clean[k] = payload[k];
    }
    await api.patch(`/admin/inventory/${slug}`, clean);
    toast.success("Inventory updated");
    setEdits((e) => { const c = { ...e }; delete c[slug]; return c; });
    load();
  };

  return (
    <div data-testid="admin-inventory-page">
      <div className="mb-6">
        <div className="eyebrow mb-2">Inventory Management</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Stock levels.</h1>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Product</th>
              <th className="text-left px-5 py-3">Stock</th>
              <th className="text-left px-5 py-3">Reserved</th>
              <th className="text-left px-5 py-3">Incoming</th>
              <th className="text-left px-5 py-3">Lot</th>
              <th className="text-left px-5 py-3">Expires</th>
              <th className="text-right px-5 py-3">Save</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const v = { ...i, ...(edits[i.peptide_slug] || {}) };
              const low = Number(v.stock) <= 10;
              return (
                <tr key={i.peptide_slug} className="border-b border-slate-100 last:border-b-0" data-testid={`inv-${i.peptide_slug}`}>
                  <td className="px-5 py-3">
                    <div className="text-slate-900">{i.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{i.peptide_slug}</div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <Input type="number" value={v.stock ?? 0} onChange={(e) => set(i.peptide_slug, "stock", e.target.value)} className="h-9 w-24 text-sm"/>
                      {low && <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.75}/>}
                    </div>
                  </td>
                  <td className="px-5 py-3"><Input type="number" value={v.reserved ?? 0} onChange={(e) => set(i.peptide_slug, "reserved", e.target.value)} className="h-9 w-20 text-sm"/></td>
                  <td className="px-5 py-3"><Input type="number" value={v.incoming ?? 0} onChange={(e) => set(i.peptide_slug, "incoming", e.target.value)} className="h-9 w-20 text-sm"/></td>
                  <td className="px-5 py-3"><Input value={v.lot ?? ""} onChange={(e) => set(i.peptide_slug, "lot", e.target.value)} className="h-9 w-32 text-sm"/></td>
                  <td className="px-5 py-3"><Input value={v.expires_at ?? ""} onChange={(e) => set(i.peptide_slug, "expires_at", e.target.value)} placeholder="YYYY-MM" className="h-9 w-28 text-sm"/></td>
                  <td className="px-5 py-3 text-right"><Button size="sm" disabled={!edits[i.peptide_slug]} onClick={() => save(i.peptide_slug)} className="h-9 bg-blue-600 hover:bg-blue-700 text-white">Save</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
